<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Save as Alias</h2>
        <button @click="$emit('close')" class="btn-close">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <div class="modal-body">
        <div class="info-box">
          <font-awesome-icon icon="fa-solid fa-circle-info" />
          <p>This will add "{{ company.name }}" as an alias to the selected company and delete this company record.</p>
        </div>

        <div class="form-group">
          <label for="targetCompany">Select Target Company <span class="required">*</span></label>
          <CompanySelector
            v-model="targetCompanyId"
            :exclude-id="company.id"
            placeholder="Select the company to add alias to..."
            required
          />
        </div>

        <div class="warning-box">
          <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
          <p><strong>Warning:</strong> This action cannot be undone. The company "{{ company.name }}" will be permanently deleted.</p>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-secondary">
          Cancel
        </button>
        <button @click="handleSaveAsAlias" :disabled="!targetCompanyId || saving" class="btn-danger">
          {{ saving ? 'Processing...' : 'Save as Alias & Delete Company' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import api from '@/utils/api';
import CompanySelector from '@/components/selectors/CompanySelector.vue';

const props = defineProps({
  company: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const targetCompanyId = ref(null);
const saving = ref(false);

const handleSaveAsAlias = async () => {
  if (!targetCompanyId.value) {
    alert('Please select a target company');
    return;
  }

  if (!confirm(`Are you sure you want to add "${props.company.name}" as an alias to the selected company and delete this company record? This action cannot be undone.`)) {
    return;
  }

  try {
    saving.value = true;

    // Add current company name as alias to target company
    const targetResponse = await api.get(`/api/companies/${targetCompanyId.value}`);
    const targetCompany = targetResponse.data;

    const aliasNames = Array.isArray(targetCompany.alias_names) ? [...targetCompany.alias_names] : [];

    // Add company name as alias if not already present
    if (!aliasNames.includes(props.company.name)) {
      aliasNames.push(props.company.name);
    }

    // Also add any existing aliases from the company being deleted
    if (Array.isArray(props.company.alias_names)) {
      props.company.alias_names.forEach(alias => {
        if (!aliasNames.includes(alias)) {
          aliasNames.push(alias);
        }
      });
    }

    // Update target company with new aliases
    await api.put(`/api/companies/${targetCompanyId.value}`, {
      ...targetCompany,
      alias_names: aliasNames
    });

    // Delete the current company
    await api.delete(`/api/companies/${props.company.id}`);
    
    // Clear from localStorage if it was the selected company
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (selectedCompanyId && parseInt(selectedCompanyId) === props.company.id) {
      localStorage.removeItem('selectedCompanyId');
    }

    alert(`Successfully added "${props.company.name}" as an alias and deleted the company.`);
    emit('saved');
  } catch (error) {
    console.error('Failed to save as alias:', error);
    alert('Failed to save as alias. Please try again.');
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.btn-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.info-box,
.warning-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.info-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.warning-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.info-box svg,
.warning-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.info-box p,
.warning-box p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: var(--danger);
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 24px;
  border-top: 1px solid var(--border);
}

.btn-secondary,
.btn-danger {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-gray);
}

.btn-danger {
  background: #dc2626;
  border: none;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
